import { useState } from "react";
import { useWizard } from "../CalendarForm";
import {
  RangeSlider,
  CardSelector,
  SectionHeader,
  TextInput,
  ParameterTrioInput,
} from "../components/inputs";

// Icons
const CutIcon = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

// Configuration options
const RHYTHM_OPTIONS = [
  { id: "daily", label: "Daily", icon: "📅" },
  { id: "weekly", label: "Weekly", icon: "📆" },
  { id: "monthly", label: "Monthly", icon: "🗓️" },
];

// Time configuration based on rhythm
const TIME_CONFIG = {
  daily: { min: 1, max: 90, unit: "days" },
  weekly: { min: 1, max: 150, unit: "weeks" },
  monthly: { min: 1, max: 60, unit: "months" },
};

// Default parameters
const DEFAULT_SKIN_FOLDS = [
  { name: "", current: "", target: "" },
  { name: "", current: "", target: "" },
];

const DEFAULT_PERIMETERS = [
  { name: "", current: "", target: "" },
  { name: "", current: "", target: "" },
];

/**
 * CutConfiguration Component
 * 
 * Step 2 configuration for fat loss.
 */
export default function CutConfiguration() {
  const { formData, updateFormData } = useWizard();

  const [config, setConfig] = useState({
    // Body measurements
    morningWeight: formData.cutConfig?.morningWeight || "",
    nightWeight: formData.cutConfig?.nightWeight || "",
    currentFatPercentage: formData.cutConfig?.currentFatPercentage || "",
    targetFatPercentage: formData.cutConfig?.targetFatPercentage || "",
    leanMassGain: formData.cutConfig?.leanMassGain || "",
    
    // Timing
    rhythm: formData.cutConfig?.rhythm || "weekly",
    time: formData.cutConfig?.time || 12,
    
    // Parameters to track
    skinFolds: formData.cutConfig?.skinFolds || DEFAULT_SKIN_FOLDS,
    perimeters: formData.cutConfig?.perimeters || DEFAULT_PERIMETERS,
  });

  const handleChange = (field, value) => {
    let newConfig = { ...config, [field]: value };
    
    // Reset time when rhythm changes
    if (field === "rhythm") {
      const timeConfig = TIME_CONFIG[value];
      newConfig.time = Math.min(Math.max(config.time, timeConfig.min), timeConfig.max);
    }
    
    setConfig(newConfig);
    updateFormData({ cutConfig: newConfig });
  };

  const handleSkinFoldChange = (index, value) => {
    const newSkinFolds = [...config.skinFolds];
    newSkinFolds[index] = value;
    handleChange("skinFolds", newSkinFolds);
  };

  const handlePerimeterChange = (index, value) => {
    const newPerimeters = [...config.perimeters];
    newPerimeters[index] = value;
    handleChange("perimeters", newPerimeters);
  };

  const timeConfig = TIME_CONFIG[config.rhythm];

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Cut Configuration"
        description="Customize your fat-loss program"
        icon={CutIcon}
        color="rose"
      />

      {/* Body Measurements Section */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
        <h4 className="text-sm font-semibold text-gray-800">Body Measurements</h4>
        
        {/* Weight inputs */}
        <div className="grid grid-cols-2 gap-4">
          <TextInput
            label="Morning Weight"
            value={config.morningWeight}
            onChange={(value) => handleChange("morningWeight", value)}
            type="number"
            placeholder="0.0"
            description="kg"
            color="rose"
          />
          <TextInput
            label="Night Weight"
            value={config.nightWeight}
            onChange={(value) => handleChange("nightWeight", value)}
            type="number"
            placeholder="0.0"
            description="kg"
            color="rose"
          />
        </div>

        {/* Fat percentage inputs */}
        <div className="grid grid-cols-2 gap-4">
          <TextInput
            label="Current Fat %"
            value={config.currentFatPercentage}
            onChange={(value) => handleChange("currentFatPercentage", value)}
            type="number"
            placeholder="0.0"
            description="%"
            color="rose"
          />
          <TextInput
            label="Target Fat %"
            value={config.targetFatPercentage}
            onChange={(value) => handleChange("targetFatPercentage", value)}
            type="number"
            placeholder="0.0"
            description="%"
            color="rose"
          />
        </div>

        {/* Lean mass gain */}
        <TextInput
          label="Lean Mass Gain Target"
          value={config.leanMassGain}
          onChange={(value) => handleChange("leanMassGain", value)}
          type="number"
          placeholder="0.0"
          description="kg"
          color="rose"
        />
      </div>

      {/* Timing Section */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
        <h4 className="text-sm font-semibold text-gray-800">Timing</h4>
        
        <CardSelector
          label="Rhythm"
          value={config.rhythm}
          onChange={(value) => handleChange("rhythm", value)}
          options={RHYTHM_OPTIONS}
          color="rose"
          columns={3}
        />

        <RangeSlider
          label="Duration"
          value={config.time}
          onChange={(value) => handleChange("time", value)}
          min={timeConfig.min}
          max={timeConfig.max}
          step={1}
          unit={` ${timeConfig.unit}`}
          color="rose"
        />
      </div>

      {/* Parameters to Track Section */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
        <h4 className="text-sm font-semibold text-gray-800">Parameters to Track</h4>
        
        <ParameterTrioInput.Group
          title="Skin Folds"
          description="Track skin fold measurements"
          parameters={config.skinFolds}
          onChange={handleSkinFoldChange}
          color="rose"
          unit="mm"
        />

        <div className="border-t border-gray-200 pt-4">
          <ParameterTrioInput.Group
            title="Body Perimeters"
            description="Track body circumference measurements"
            parameters={config.perimeters}
            onChange={handlePerimeterChange}
            color="rose"
            unit="cm"
          />
        </div>
      </div>
    </div>
  );
}